import React from "react"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Container, FormGroup, Input, Label, Spinner, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { eventLabel } from "pages/EventsManagement/EventDetailsOverview"
import { plural, quantityLabel, supplierNames } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import { dayLabel } from "pages/EventsManagement/components/Tabs/Logistics/shipmentLabels"
import OrganizerLink from "pages/EventsManagement/components/Tabs/Logistics/OrganizerLink"
import useXlsxDownload from "pages/EventsManagement/components/Tabs/Logistics/useXlsxDownload"
import { Choice, Period, ReadError, useAddressRead } from "../addressFilters"
import { FILTERS, datesLabel, eventStateLabel, exportParams, productLabel, sheetStates, signalColor, signalLabel } from "./sheetLabels"
import "pages/EventsManagement/components/Tabs/Logistics/logistics.scss"
import "./sheets.scss"

const FAILED = "The sheets could not be loaded."

const Check = ({ id, label, checked, onChange }) => <FormGroup check className="logistics-sheets-check mb-0">
  <Input id={id} type="checkbox" checked={checked} onChange={event => onChange(event.target.checked)} />
  <Label check for={id}>{label}</Label>
</FormGroup>

// One row per event: its days, club and organizer, category and sport, the sheet's state, what it
// still needs and its shipments, with the day its main shipment left; the name opens the event's Logistics tab
// and the organizer's name the organizer's profile.
const EventsTable = ({ events }) => <Table size="sm" className="logistics-stack mb-0">
  <thead><tr>
    <th>Event</th><th>Club · organizer</th><th>Category · sport</th><th>Sheet</th><th>Signals</th><th className="text-right">Shipments</th>
  </tr></thead>
  <tbody>{events.map(event => <tr key={event.id}>
    <td><Link to={`/events/${event.id}/logistics`}><strong>{event.name || "Unnamed event"}</strong></Link>
      {eventStateLabel(event.status) && <Badge color="soft-secondary" className="ml-1">{eventStateLabel(event.status)}</Badge>}
      <div className="small text-muted">{datesLabel(event.startDate, event.endDate)}{event.eventId ? ` · #${event.eventId}` : ""}</div></td>
    <td data-label="Club · organizer">{event.club?.name || "No club"}
      <div className="small text-muted"><OrganizerLink organizer={event.organizer}>{event.organizer?.name || "No organizer"}</OrganizerLink></div></td>
    {/* A Tenis10 event is tennis with the programme (PRD 16 LR15): named Tenis10, as on its event page. */}
    <td data-label="Category · sport">{eventLabel(event.category)}<div className="small text-muted">{event.programType === "tenis10" ? "Tenis10"
      : eventLabel(event.sportType)}</div></td>
    <td data-label="Sheet"><Badge color={sheetStates[event.sheet.status]?.color || "soft-secondary"}>
      {sheetStates[event.sheet.status]?.label || event.sheet.status}</Badge></td>
    <td data-label="Signals">{event.signals.length ? event.signals.map(signal => <Badge key={signal.kind} color={signalColor(signal)}
      className="logistics-sheets-signal">{signalLabel(signal)}</Badge>) : "—"}</td>
    <td className="text-right" data-label="Shipments">{event.shipments || "—"}
      {event.mainShipment && <div className="small text-muted">main {dayLabel(event.mainShipment)}</div>}</td>
  </tr>)}</tbody>
</Table>

// One row per product and place (and supplier): the effective quantities of the listed sheets, and
// what left the warehouse for them; the club's lines never leave it (—).
const ProductsTable = ({ products }) => <>
  <Table size="sm" className="logistics-stack mb-0">
    <thead><tr>
      <th>Product</th><th className="text-right">Effective</th><th className="text-right">Shipped</th><th className="text-right">Returned</th>
      <th className="text-right">Consumption</th><th className="text-right">Current balance</th>
    </tr></thead>
    <tbody>{products.map(row => <tr key={row.key}>
      <td><strong>{productLabel(row)}</strong>
        {row.supplier !== "mpg" && <Badge color="soft-secondary" className="ml-1">{supplierNames[row.supplier] || "No supplier"}</Badge>}</td>
      <td className="text-right" data-label="Effective">{quantityLabel(row.effective, row.unit)}
        <div className="small text-muted">{[plural(row.events, "event"), row.incomplete ? `${row.incomplete} to complete` : null].filter(Boolean).join(" · ")}</div>
      </td>
      <td className="text-right" data-label="Shipped">{row.shipped == null ? "—" : <>{row.shipped}<div className="small text-muted">incl. supplementary</div></>}</td>
      <td className="text-right" data-label="Returned">{row.returned || "—"}</td>
      <td className="text-right" data-label="Consumption">{row.consumption == null ? "—" : <strong>{row.consumption}</strong>}</td>
      <td className="text-right" data-label="Current balance">{!row.product || row.supplier !== "mpg" ? "—"
        : row.balance ?? "—"}</td>
    </tr>)}</tbody>
  </Table>
  <p className="small text-muted mt-2 mb-0">No “Calculated” column: on shipped sheets it is recalculated with the current rules, so its sum would mislead.
    The current balance is informative; need and alerts stay in Warehouse.</p>
</>

// Logistics → Sheets (Centralizatorul logistic, US 131–134, ticket 18): the sheets of the events
// starting in a period, by event or by product, with the filters in the address, and their XLSX.
export default function LogisticsSheets() {
  const { params, answer, ready, error, retry, setParam, replaceSearch } = useAddressRead("/admin/logistics/sheets", FILTERS, FAILED)
  const byProduct = params.get("view") === "products"
  const download = useXlsxDownload()

  const options = answer?.options || { clubs: [], organizers: [], categories: [], sports: [] }
  const reset = () => replaceSearch(new URLSearchParams(byProduct ? { view: "products" } : {}))
  const checked = name => params.get(name) === "true"

  // The XLSX of the filter the page shows.
  const exportXlsx = () => ready && download.download("/exports/logistics/sheets", { params: exportParams(answer.filter) },
    `logistics-sheets-${answer.filter.from}-${answer.filter.to}.xlsx`)

  const count = ready ? answer.events.length : 0
  const note = byProduct ? `Totals of the ${plural(count, "filtered event")} · ${answer?.filter?.clubLines ? "MPG and club lines" : "MPG lines"}`
    : `${plural(count, "event")} · ordered by start date · a row opens the event’s Logistics tab`

  return <div className="page-content logistics-sheets">
    <Container fluid>
      <Breadcrumbs title="Logistics" breadcrumbItem="Sheets" path="/logistics/sheets" />
      <div className="logistics-toolbar">
        <ButtonGroup aria-label="View">
          <Button size="sm" color="primary" outline={byProduct} aria-pressed={!byProduct} onClick={() => setParam("view", "")}>By event</Button>
          <Button size="sm" color="primary" outline={!byProduct} aria-pressed={byProduct} onClick={() => setParam("view", "products")}>By product</Button>
        </ButtonGroup>
        <Button size="sm" color="light" onClick={exportXlsx} disabled={!ready || download.busy}>
          <i className="bx bx-download mr-1" aria-hidden="true" />{download.busy ? "Preparing XLSX…" : "Export XLSX"}
        </Button>
      </div>
      {download.error && <Alert color="danger" isOpen toggle={download.dismiss}>{download.error}</Alert>}
      <ReadError error={error} retry={retry} />

      <Card tag="section" aria-label="Filters"><CardBody className="logistics-filters">
        <Period id="logistics-sheets-period" label="Period (start date)" from={params.get("from") || answer?.filter?.from || ""}
          to={params.get("to") || answer?.filter?.to || ""} onChange={setParam} />
        <Choice id="logistics-sheets-club" label="Club" all="All clubs" missing="Club without sheets in this period" value={params.get("clubId") || ""}
          choices={options.clubs.map(club => ({ value: club.id, label: club.name || "Unnamed club" }))} onChange={value => setParam("clubId", value)} />
        <Choice id="logistics-sheets-organizer" label="Organizer" all="All organizers" missing="Organizer without sheets in this period"
          value={params.get("organizerId") || ""} onChange={value => setParam("organizerId", value)}
          choices={options.organizers.map(organizer => ({ value: organizer.id, label: organizer.name || "Unnamed organizer" }))} />
        <Choice id="logistics-sheets-category" label="Category" all="All" missing={eventLabel(params.get("category"))}
          value={params.get("category") || ""} choices={options.categories.map(category => ({ value: category, label: eventLabel(category) }))}
          onChange={value => setParam("category", value)} />
        <Choice id="logistics-sheets-sport" label="Sport" all="All" missing={eventLabel(params.get("sportType"))} value={params.get("sportType") || ""}
          choices={options.sports.map(sport => ({ value: sport, label: eventLabel(sport) }))} onChange={value => setParam("sportType", value)} />
        <Choice id="logistics-sheets-state" label="Sheet" all="All" missing={params.get("status")} value={params.get("status") || ""}
          choices={Object.entries(sheetStates).map(([value, { label }]) => ({ value, label }))} onChange={value => setParam("status", value)} />
        <Check id="logistics-sheets-signals" label="Only with signals" checked={checked("withSignals")} onChange={value => setParam("withSignals", value)} />
        <Check id="logistics-sheets-disabled" label="Include disabled" checked={checked("includeDisabled")} onChange={value => setParam("includeDisabled", value)} />
        {byProduct && <Check id="logistics-sheets-club-lines" label="Include club lines" checked={checked("clubLines")}
          onChange={value => setParam("clubLines", value)} />}
        <Button color="link" size="sm" className="px-0" onClick={reset}>Reset filters</Button>
      </CardBody></Card>

      <Card tag="section" aria-label="Sheets"><CardBody>
        {ready && <p className="text-muted small">{note}</p>}
        {!ready && !error && <div className="text-center my-3"><Spinner color="primary" size="sm" /></div>}
        {ready && (byProduct ? (answer.products.length ? <ProductsTable products={answer.products} /> : <p className="text-muted mb-0">No line matches these filters.</p>)
          : answer.events.length ? <EventsTable events={answer.events} /> : <p className="text-muted mb-0">No sheet matches these filters.</p>)}
      </CardBody></Card>
    </Container>
  </div>
}
